import type { LDProps } from 'launchdarkly-react-client-sdk';
import type { FragmentType } from 'src/gql';
import type { ClientContact } from 'src/types/client';
import type { Household } from 'src/types/household';
import type { SideBarItemProps } from 'src/utils/advisor-dash-nav/side-bar';
import { useEffect, useMemo, useState } from 'react';
import NextLink from 'next/link';
import { usePathname } from 'next/navigation';
import { useRouter } from 'next/router';
import { Button } from '@savvy-kit/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@savvy-kit/components/dropdown-menu';
import IconButton from '@savvy-kit/components/icon-button';
import { Popover, PopoverContent, PopoverTrigger } from '@savvy-kit/components/popover';
import { TAB_VARIANT_TOGGLE } from '@savvy-kit/components/tabs';
import { Text } from '@savvy-kit/foundations/text';
import PageTitle from '@savvy-kit/templates/page-title';
import { isNil } from 'es-toolkit';
import { useFlags, withLDConsumer } from 'launchdarkly-react-client-sdk';
import { ChevronDown, EllipsisIcon, Info as InfoIcon, Pencil, Settings } from 'lucide-react';
import { isScV3Enabled } from 'src/components/advisor-dashboard/service-calendar-v3/flags';
import { TrackedButton, TrackedDropdownMenuItem } from 'src/components/tracked';
import { HouseholdOnboardingStatusRibbon } from 'src/features/advisor-book-onboarding/components/household-onboarding-status-ribbon';
import { HouseholdOnboardingStatusRibbonRowFragment } from 'src/features/advisor-book-onboarding/components/household-onboarding-status-ribbon-gql';
import { useCheckHouseholdKycMissingDetails } from 'src/features/upgrade-custodian/api/use-household-kyc-missing-details';
import { UpgradeCustodianMissingDetailsModal } from 'src/features/upgrade-custodian/components/upgrade-custodian-missing-details-modal';
import { getUpgradeCustodianStepHref } from 'src/features/upgrade-custodian/constants/upgrade-custodian';
import { graphql, unmaskFragment } from 'src/gql';
import { isProspect } from 'src/types/household';
import { canViewAdminFeatures } from 'src/utils/admin';
import { sortedActiveItems } from 'src/utils/advisor-dash-nav/side-bar';
import { getAdvisorFullName } from 'src/utils/advisor-dash/advisor';
import { formatDollarAmount, formatLargeDollarAmount, formatTimestampToRelative } from 'src/utils/formatting';

import type { Tab as LinkTab } from '@savvy-kit/patterns/link-tabs';
import {
  ADVICE_ONLY_CLIENT_ARR_TOOLTIP_TEXT,
  ARR_TOOLTIP_TEXT,
} from '@savvy/constants/content-strings/advisor-dashboard';
import { HOUSEHOLD_STAGE_INACTIVE } from '@savvy/constants/household-stages';
import { USER_ROLE_ASSISTANT } from '@savvy/constants/user-roles';
import { useAuth } from '@savvy/deprecated-shared/hooks/use-auth';
import { useLazyQueryWithErrorLogging } from '@savvy/graphql-hooks/useLazyQueryWithErrorLogging';
import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';

import type { HouseholdTagRow } from '../../tags/household-tags-row';
import { EditAdvisorDialog } from '../../account-detail/edit-advisor-dialog';
import { ArchivedHouseholdBanner } from '../../account-overview/archived-household-banner';
import { useHouseholdPageRevampEnabled } from '../../account-overview/use-household-page-revamp-enabled';
import { useHouseholdOverviewRailPreference } from '../../layout/household-overview-rail-preference-context';
import { usePageBreadcrumbs } from '../../layout/page-breadcrumb-context';
import { HouseholdTagsInline } from '../../tags/household-tags-inline';
import { HouseholdTagsRow } from '../../tags/household-tags-row';
import { ManageHouseholdTagsDialog } from '../../tags/manage-household-tags-dialog';
import AdminActionButtons from './admin-action-buttons';
import { applyTabOrder, getSideBarItems } from './client-page-nav-items';
import { HouseholdAccountFragment } from './client-page-title-nav-gql';
import { CustomizeHouseholdViewDialog } from './customize-household-view/customize-household-view-dialog';
import { clientSectionPath, getActiveTab, OVERVIEW_TAB_TITLE, sectionMatches } from './get-active-tab';
import { ContactWebLauncher } from './household-header/contact-web-launcher';
import { HouseholdHeaderActions } from './household-header/household-header-actions';
import LogInAsClientButton from './log-in-as-client-button';
import { ResetDemoHouseholdMenuItem } from './reset-demo-household-button';
import { useApolloNetworkQuiet } from './use-apollo-network-quiet';
import { useHouseholdLandingTab } from './use-household-landing-tab';

// Separate from the main household query so that Organization auth failures (which occur when an
// advisor accesses a cross-org shared household) don't break the whole page — only tags are affected.
const HOUSEHOLD_ORGANIZATION_TAGS_QUERY = graphql(/* GraphQL */ `
  query HouseholdOrganizationTags($salesforceAccountId: String!) {
    household(salesforceAccountId: $salesforceAccountId) {
      advisor {
        id
        organization {
          id
          organizationHouseholdCategories {
            id
            name
            hexColorCode
            emoji
            organizationHouseholdTags {
              id
              name
              isSmart
              householdCount
            }
          }
        }
      }
      householdTags {
        id
        organizationHouseholdTag {
          id
          name
          isSmart
          organizationHouseholdCategory {
            hexColorCode
            emoji
          }
        }
      }
    }
  }
`);

export const ADVISOR_DASH_CLIENT_QUERY = graphql(/* GraphQL */ `
  query ClientForAdvisorDash($salesforceAccountId: String!) {
    client(salesforceAccountId: $salesforceAccountId) {
      id
      ...GetSideBarItems
      ...LogInAsClientButton
      assignedAdvisor {
        id
        salesforceUserId
        user {
          id
          firstName
          lastName
          email
          role
        }
        advisorGroups {
          id
          name
          representationType
        }
      }
      user {
        id
        profilePicUrl
        role
        fullName
      }
      household {
        id
        householdStage
        lastContactAt
        name
        portfolios {
          id
          householdId
          name
          displayName
          isPersonal
          orionHouseholdId
          portfolioStrategies {
            id
            portfolioId
            strategyName
          }
        }
        ...AdminActionButtons
      }
    }
  }
`);

export function disabledItemTooltipTitle(title?: string) {
  if (title === 'Portfolio') {
    return 'Available once the prospect expresses interested in becoming a client and signs the Terms of Service';
  } else if (title === 'Net worth') {
    return 'Available once the prospect expresses interested in becoming a client and signs the Terms of Service';
  }
  return 'Available once the client has completed onboarding';
}

type ClientPageTitleNavProps = LDProps & {
  readonly householdFragment?: FragmentType<typeof HouseholdAccountFragment> | null;
  readonly refetchHouseholdData: () => void;
  readonly salesforceAccountId: string;
};

function ClientPageTitleNav({ householdFragment, refetchHouseholdData, salesforceAccountId }: ClientPageTitleNavProps) {
  const pathname = usePathname();
  const router = useRouter();
  const { pageBreadcrumbs, pageTitle, pageTitleSuffix, pageTitleLoading, hideActiveTabLabel } = usePageBreadcrumbs();

  const { user } = useAuth();
  const flags = useFlags();
  const { flagEnableUpgradeCustodian, flagDocusignStatusTracker, flagAdvisorOnboarding, flagFinancialPlanner } = flags;
  const isHouseholdPageRevampEnabled = useHouseholdPageRevampEnabled();
  const { tabOrder, isSettled: isSavedViewSettled } = useHouseholdOverviewRailPreference();

  // Read here, not inside the cluster: the cluster unmounts and remounts when the header
  // collapse moves it between the title and breadcrumb rows, which would restart this latch
  // and leave "Log in as client" disabled for another ~600ms. This component is stable across
  // that boundary. Gated on the flag so the flag-off path polls exactly as much as it does today.
  const pageReady = useApolloNetworkQuiet(isHouseholdPageRevampEnabled);

  const [fetchClientData, { data: clientData }] = useLazyQueryWithErrorLogging(ADVISOR_DASH_CLIENT_QUERY, {
    variables: {
      salesforceAccountId,
    },
  });

  // Separate from the main household query so that Organization auth failures (which occur when an
  // advisor accesses a cross-org shared household) don't break the whole page — only tags are affected.
  const { data: householdTagsData } = useQueryWithErrorLogging(HOUSEHOLD_ORGANIZATION_TAGS_QUERY, {
    variables: { salesforceAccountId },
  });
  const householdWithTags = householdTagsData?.household;

  const household = householdFragment ? unmaskFragment(HouseholdAccountFragment, householdFragment) : null;
  const primaryContact = household?.primaryClient;
  const coClientContact = household?.coClient;

  const personalPortfolio = clientData?.client.household.portfolios.find(
    (portfolio: { isPersonal: boolean }) => portfolio.isPersonal,
  );
  const secondaryPortfolios = clientData?.client.household.portfolios.filter(
    (portfolio: { isPersonal: boolean }) => !portfolio.isPersonal,
  );

  const sideBarItems = getSideBarItems({
    clientFrag: clientData?.client,
    personalPortfolioId: personalPortfolio?.id,
    secondaryPortfolios,
    household: household || undefined,
    flags: {
      docusignStatusTracker: Boolean(flagDocusignStatusTracker),
      financialPlanner: Boolean(flagFinancialPlanner),
      serviceCalendarV3: isScV3Enabled(flags),
    },
  });

  const shouldShowLastContactDate =
    !isNil(household?.primaryClient.user.id) && !isNil(clientData?.client.household.lastContactAt);
  const shouldShowBillableAssets = !isNil(household) && !isProspect(household) && !isNil(household.savvyAum);
  const shouldShowAnnualRevenue = user?.role !== USER_ROLE_ASSISTANT && !isNil(household?.advisorAnnualRevenue);
  const shouldShowPotentialAUM =
    !isNil(household) && isProspect(household) && !isNil(household.prospectData?.estimatedAum);

  const shouldShowClientInfoTooltip =
    shouldShowLastContactDate ||
    shouldShowBillableAssets ||
    shouldShowAnnualRevenue ||
    shouldShowPotentialAUM ||
    canViewAdminFeatures(user);

  // show-login-as-button-for-co-client w/ emails only
  const shouldShowLoginAsCoClientButton = coClientContact?.user.email !== undefined;

  const [showEditAdvisorDialog, setShowEditAdvisorDialog] = useState(false);
  const [manageTagsOpen, setManageTagsOpen] = useState(false);
  const [showMissingDetailsModal, setShowMissingDetailsModal] = useState(false);
  const [customizeViewOpen, setCustomizeViewOpen] = useState(false);

  const isArchived = household?.householdStage === HOUSEHOLD_STAGE_INACTIVE;
  const isDemo = !!household?.demo;

  const hasActiveEciOnboarding = household?.hasActiveEciOnboarding ?? false;
  const showUpgradeCustodian = flagEnableUpgradeCustodian && !hasActiveEciOnboarding;
  const isMenuEnabled = isDemo || showUpgradeCustodian;
  const bookOnboardingCockpit = household?.bookOnboardingCockpit;

  const primaryClientUserId = primaryContact?.user.id;
  const coClientUserId = coClientContact?.user.id;
  const checkHouseholdKycMissingDetails = useCheckHouseholdKycMissingDetails();

  const handleUpgradeCustodianClick = () => {
    void checkHouseholdKycMissingDetails({ primaryClientUserId, coClientUserId }).then((hasMissingDetails) => {
      if (hasMissingDetails) {
        setShowMissingDetailsModal(true);
      } else {
        void router.push(getUpgradeCustodianStepHref(salesforceAccountId, 'select-accounts'));
      }
    });
  };

  useEffect(() => {
    if (!household) return;
    if (household.primaryClient.user.id) void fetchClientData();
  }, [household]);

  const editAdvisor = () => {
    setShowEditAdvisorDialog(true);
  };

  const onHouseholdStageChanged = () => {
    refetchHouseholdData();
  };

  const getTabDetails = (item: SideBarItemProps) => {
    // Items with sub-navigation render as a dropdown (e.g. Portfolio → Net worth, Investments, Strategy)
    if (item.items && item.items.length > 0) {
      return {
        name: (
          <DropdownMenu>
            <DropdownMenuTrigger render={<div className="flex items-center gap-1 capitalize" />}>
              <span>{item.title}</span>
              <ChevronDown className="size-3" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {item.items.map((subItem) => (
                <DropdownMenuItem
                  key={subItem.path}
                  disabled={subItem.disabled}
                  render={<NextLink href={String(subItem.path)} />}
                >
                  {subItem.title}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        ),
        value: String(item.path),
        disabled: item.disabled,
        disabledTooltip: item.disabled ? item.disabledTooltip || disabledItemTooltipTitle(item.title) : undefined,
        showBadge: item.showBadge,
      };
    }

    return {
      name: (
        <NextLink href={String(item.path)} className="capitalize">
          {item.title}
        </NextLink>
      ),
      value: String(item.path),
      disabled: item.disabled,
      disabledTooltip: item.disabled ? item.disabledTooltip || disabledItemTooltipTitle(item.title) : undefined,
      showBadge: item.showBadge,
    };
  };

  const getPageTitleTabs = (navItems: SideBarItemProps[]): LinkTab[] => {
    const tabs: LinkTab[] = navItems.map((item) => getTabDetails(item));

    if (household && canViewAdminFeatures(user)) {
      tabs.push({
        name: (
          <Popover>
            <PopoverTrigger render={<div className="flex items-center gap-1 text-secondary hover:text-dark" />}>
              <span>Admin</span>
              <ChevronDown />
            </PopoverTrigger>
            <PopoverContent className="w-[276px] flex flex-col gap-2">
              <AdminActionButtons household={household} householdFrag={clientData?.client.household} />
            </PopoverContent>
          </Popover>
        ),
        value: 'admin-dropdown',
      });
    }

    return tabs;
  };

  // Declared order, not the rendered one: the dialog's Tabs pane seeds from what the nav would show
  // with nothing customized.
  const navTitles = sideBarItems.map((item) => item.title);
  // Gated alongside the gear and the dialog — a flag rollback must not leave a reordered nav with
  // no affordance left to reset it.
  const clientNavItems = sortedActiveItems(applyTabOrder(sideBarItems, isHouseholdPageRevampEnabled ? tabOrder : []));
  const pageTitleTabs = getPageTitleTabs(clientNavItems);

  useHouseholdLandingTab({
    enabled: isHouseholdPageRevampEnabled,
    // Compared against `household.id` rather than merely awaited: the lazy client query keeps the
    // previous household's result while the next one is in flight, and Portfolio's path carries
    // `personalPortfolioId` off it.
    ready: isSavedViewSettled && !isNil(household) && clientData?.client.household.id === household.id,
    navItems: clientNavItems,
    householdPublicId: salesforceAccountId,
  });

  // Find the active tab or sub-item title based on pathname for breadcrumb display
  const {
    activeTabLabel: rawActiveTabLabel,
    isSubPage,
    activeTabPath,
  } = useMemo(() => {
    if (!pathname) return { activeTabLabel: null, isSubPage: false, activeTabPath: null };
    const currentSection = clientSectionPath(pathname);
    if (currentSection === null) return { activeTabLabel: null, isSubPage: false, activeTabPath: null };
    for (const item of clientNavItems) {
      // Check sub-items first (e.g. Net worth, Managed investments, Strategy under Portfolio)
      if (item.items) {
        const matchedSubItem = item.items.find((sub) => sectionMatches(currentSection, sub.path));
        if (matchedSubItem) {
          return {
            activeTabLabel: matchedSubItem.title,
            isSubPage: true,
            activeTabPath: matchedSubItem.path ?? null,
          };
        }
      }
      // Check top-level items, but skip Overview (it's the default)
      if (item.title !== OVERVIEW_TAB_TITLE && sectionMatches(currentSection, item.path)) {
        return { activeTabLabel: item.title, isSubPage: false, activeTabPath: item.path };
      }
    }
    return { activeTabLabel: null, isSubPage: false, activeTabPath: null };
  }, [pathname, clientNavItems]);

  // Pages can opt out of having their active sidebar-tab label show in the
  // breadcrumb when their own title already names the section. Treat the
  // value as null for layout + breadcrumb purposes when the flag is on.
  const activeTabLabel = hideActiveTabLabel ? null : rawActiveTabLabel;

  const { availableTags, popularSuggestions } = useMemo(() => {
    const categories = householdWithTags?.advisor?.organization?.organizationHouseholdCategories ?? [];
    const all: {
      id: string;
      name: string;
      hexColorCode: string | null;
      emoji: string | null;
      isSmart: boolean;
      householdsCount: number;
    }[] = [];
    for (const cat of categories) {
      const hexColorCode = typeof cat.hexColorCode === 'string' ? cat.hexColorCode : null;
      const emoji = typeof cat.emoji === 'string' ? cat.emoji : null;
      const tags = cat.organizationHouseholdTags ?? [];
      for (const t of tags) {
        const count = typeof t.householdCount === 'number' ? t.householdCount : 0;
        all.push({
          id: t.id,
          name: t.name,
          hexColorCode,
          emoji,
          isSmart: t.isSmart,
          householdsCount: count,
        });
      }
    }
    const allSimple = all.map(({ id, name, hexColorCode, emoji, isSmart }) => ({
      id,
      name,
      hexColorCode,
      emoji,
      isSmart,
    }));
    const popular = [...all]
      .sort((a, b) => b.householdsCount - a.householdsCount)
      .slice(0, 10)
      .map(({ id, name, hexColorCode, emoji, isSmart }) => ({ id, name, hexColorCode, emoji, isSmart }));
    return { availableTags: allSimple, popularSuggestions: popular };
  }, [householdWithTags]);

  // Applied tags in the flat shape that both the inline `HouseholdTagsRow`
  // (above the tab bar) and the `ManageHouseholdTagsDialog` (opened by the
  // row's Edit action) consume. Hoisted here so both stay in sync from a
  // single derivation. See Linear 4C1T-115.
  const appliedTags = useMemo<readonly HouseholdTagRow[]>(() => {
    const result: HouseholdTagRow[] = [];
    const list = householdWithTags?.householdTags ?? [];
    for (const ht of list) {
      const t = ht.organizationHouseholdTag;

      result.push({
        id: String(t.id),
        name: String(t.name),
        hexColorCode: t.organizationHouseholdCategory.hexColorCode ?? null,
        emoji: t.organizationHouseholdCategory.emoji ?? null,
        isSmart: t.isSmart,
      });
    }
    return result;
  }, [householdWithTags]);

  const adminAdvisorControl =
    household && canViewAdminFeatures(user) ? (
      <>
        <Button size="sm" icon={<Pencil />} variant="ghost" iconPosition="right" onClick={editAdvisor}>
          <div className="flex items-center gap-1">
            <span className="text-secondary">Advisor</span>
            <span>{getAdvisorFullName(clientData)}</span>
          </div>
        </Button>
        {showEditAdvisorDialog && household.primaryClient.user.id && clientData?.client.assignedAdvisor && (
          <EditAdvisorDialog
            setShowEditAdvisorDialog={setShowEditAdvisorDialog}
            user_id={String(household.primaryClient.user.id)}
            current_advisor={clientData.client.assignedAdvisor}
          />
        )}
      </>
    ) : undefined;

  // Paired with `collapseBelowHeader`: what the tags become once the full-width row below the
  // header has collapsed away. Both stay mounted across the threshold — the row keeps its
  // Hide/Show state and this keeps its popover, so neither is torn down mid-scroll.
  const householdTagsInline =
    isHouseholdPageRevampEnabled && !isNil(householdWithTags) && !isNil(household) ? (
      <HouseholdTagsInline tags={appliedTags} disabled={isArchived} onManage={() => setManageTagsOpen(true)} />
    ) : undefined;

  // Goes through titleRowActions, not pageTitleExtra: pageTitleExtra sits inside
  // page-title's collapse grid, which is fully collapsed on every tab-level page.
  // Keeping it out of actionButtons is what lets the notifications bell reach the
  // far right — page-title drops that slot's wrapper entirely when it is empty.
  const householdHeaderCluster = isHouseholdPageRevampEnabled ? (
    <HouseholdHeaderActions
      clientFrag={clientData?.client}
      flagEnableUpgradeCustodian={Boolean(flagEnableUpgradeCustodian)}
      hasActiveEciOnboarding={hasActiveEciOnboarding}
      household={household}
      isArchived={isArchived}
      pageReady={pageReady}
      onUpgradeCustodianClick={handleUpgradeCustodianClick}
    />
  ) : undefined;

  return (
    <>
      {isArchived && (
        <div className="flex justify-center mx-8 mt-6">
          <div className="w-full max-w-[1400px]">
            <ArchivedHouseholdBanner
              householdId={household.id}
              householdName={household.name}
              onHouseholdStageChanged={onHouseholdStageChanged}
            />
          </div>
        </div>
      )}
      <PageTitle
        collapsible={!activeTabLabel || isSubPage || pageBreadcrumbs.length > 0}
        showTitle={!activeTabLabel || isSubPage || pageBreadcrumbs.length > 0}
        loading={
          (!activeTabLabel && pageBreadcrumbs.length === 0 && isNil(pageTitle) && isNil(household?.name)) ||
          (pageTitleLoading && isNil(pageTitle))
        }
        pageTitle={
          pageTitle ??
          (() => {
            if (!activeTabLabel && pageBreadcrumbs.length === 0) {
              if (household?.name) {
                return String(household.name);
              }

              return '';
            }

            if (isSubPage) {
              return activeTabLabel ?? '';
            }

            return '';
          })()
        }
        pageTitleSuffix={pageTitleSuffix}
        breadcrumbs={[
          { label: 'Clients', href: '/advisor/dashboard/clients' },
          ...(() => {
            if (pageBreadcrumbs.length > 0) {
              return [
                {
                  label: household?.name ? String(household.name) : '',
                  href: `/advisor/dashboard/client/${salesforceAccountId}`,
                  loading: isNil(household?.name),
                },
                ...pageBreadcrumbs,
              ];
            }

            if (activeTabLabel) {
              if (pageTitleLoading) {
                return [
                  {
                    label: household?.name ? String(household.name) : '',
                    href: `/advisor/dashboard/client/${salesforceAccountId}`,
                    loading: isNil(household?.name),
                  },
                  {
                    label: activeTabLabel,
                    href: activeTabPath ?? undefined,
                  },
                  { label: '', loading: true },
                ];
              }

              return [
                {
                  label: household?.name ? String(household.name) : '',
                  href: `/advisor/dashboard/client/${salesforceAccountId}`,
                  loading: isNil(household?.name),
                },
                { label: activeTabLabel },
              ];
            }

            return [
              {
                label: household?.name ? String(household.name) : '',
                loading: isNil(household?.name),
              },
            ];
          })(),
        ]}
        Icon={
          !activeTabLabel && pageBreadcrumbs.length === 0 && household && shouldShowClientInfoTooltip
            ? InfoIcon
            : undefined
        }
        iconHoverContent={
          household && shouldShowClientInfoTooltip ? (
            <div className="flex flex-col gap-2">
              {canViewAdminFeatures(user) && (
                <>
                  <SavvyHouseholdIdDisplay householdId={clientData?.client.household.id} />
                  <PrimaryClientUserIdDisplay userId={household.primaryClient.user.id} />
                </>
              )}
              {shouldShowLastContactDate && (
                <HouseholdLastContactDate lastContactDate={clientData.client.household.lastContactAt} />
              )}
              {shouldShowBillableAssets && <BillableAssetsDisplay household={household} />}
              {shouldShowAnnualRevenue && <AnnualRevenueDisplay household={household} />}
              {shouldShowPotentialAUM && <PotentialAUMDisplay household={household} />}
              {!canViewAdminFeatures(user) &&
                !shouldShowLastContactDate &&
                !shouldShowBillableAssets &&
                !shouldShowAnnualRevenue &&
                !shouldShowPotentialAUM && (
                  <Text variant="body-sm" color="secondary">
                    No additional client information available
                  </Text>
                )}
            </div>
          ) : undefined
        }
        pageTitleExtra={
          isHouseholdPageRevampEnabled ? undefined : (
            <>
              {adminAdvisorControl}
              {!isNil(household) && household.salesforceAccountId && (
                <TrackedButton
                  size="sm"
                  variant="secondary"
                  elementName="open-in-canvas-button"
                  className="py-0"
                  render={<NextLink href={`/advisor/canvas/household/${household.salesforceAccountId}`} />}
                >
                  Open in Canvas
                </TrackedButton>
              )}
              <ContactWebLauncher householdId={household?.id} />
              {primaryContact && (
                <LogInAsClientButton
                  disabled={isArchived}
                  clientFrag={clientData?.client}
                  contact={
                    {
                      Id: primaryContact.id,
                      FirstName: primaryContact.user.firstName,
                      LastName: primaryContact.user.lastName,
                      Email: primaryContact.user.email,
                      Savvy_User_Id__c: primaryContact.user.id,
                      XLR8CS__Primary_Contact__c: true,
                      Co_Client__c: false,
                    } satisfies ClientContact
                  }
                  co_contact={
                    coClientContact && shouldShowLoginAsCoClientButton
                      ? ({
                          Id: coClientContact.id,
                          FirstName: coClientContact.user.firstName,
                          LastName: coClientContact.user.lastName,
                          Email: coClientContact.user.email,
                          Savvy_User_Id__c: coClientContact.user.id,
                          XLR8CS__Primary_Contact__c: false,
                          Co_Client__c: true,
                        } satisfies ClientContact)
                      : null
                  }
                />
              )}
              {isMenuEnabled && !isNil(household) && (
                <DropdownMenu>
                  <DropdownMenuTrigger render={<IconButton variant="ghost" />}>
                    <EllipsisIcon />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="flex flex-col w-[220px]">
                    {showUpgradeCustodian && (
                      <TrackedDropdownMenuItem
                        elementName="upgrade-custodian-menu-item"
                        onClick={handleUpgradeCustodianClick}
                      >
                        Upgrade custodian
                      </TrackedDropdownMenuItem>
                    )}
                    {isDemo && !isNil(household) && <ResetDemoHouseholdMenuItem householdId={household.id} />}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </>
          )
        }
        titleRowActions={householdHeaderCluster}
        breadcrumbRowExtra={householdTagsInline}
        collapseBelowHeader={isHouseholdPageRevampEnabled}
        actionButtons={isHouseholdPageRevampEnabled ? adminAdvisorControl : undefined}
        tabVariant={TAB_VARIANT_TOGGLE}
        tabsProps={{
          activeTab: getActiveTab(clientNavItems, pathname || ''),
          tabs: pageTitleTabs,
        }}
        tabRowActions={
          household && isHouseholdPageRevampEnabled ? (
            <button
              type="button"
              className="flex items-center text-secondary hover:text-dark"
              aria-label="Customize view"
              onClick={() => setCustomizeViewOpen(true)}
            >
              <Settings className="size-4" aria-hidden="true" />
            </button>
          ) : undefined
        }
        belowHeader={
          !isNil(householdWithTags) && !isNil(household) ? (
            <HouseholdTagsRow
              householdId={String(household.id)}
              tags={appliedTags}
              disabled={isArchived}
              onEdit={() => setManageTagsOpen(true)}
            />
          ) : undefined
        }
      />
      {flagAdvisorOnboarding === true && !isNil(bookOnboardingCockpit) && (
        <HouseholdOnboardingStatusRibbon
          row={unmaskFragment(HouseholdOnboardingStatusRibbonRowFragment, bookOnboardingCockpit)}
          onDismissed={refetchHouseholdData}
          onContactInfoResolved={refetchHouseholdData}
          onMarkedComplete={refetchHouseholdData}
          onInviteResent={refetchHouseholdData}
        />
      )}
      <ManageHouseholdTagsDialog
        open={manageTagsOpen}
        onOpenChange={setManageTagsOpen}
        householdId={household?.id ? String(household.id) : ''}
        householdName={household?.name ?? undefined}
        tags={appliedTags}
        suggestions={popularSuggestions}
        availableTags={availableTags}
      />
      {household && isHouseholdPageRevampEnabled && (
        <CustomizeHouseholdViewDialog
          open={customizeViewOpen}
          onOpenChange={setCustomizeViewOpen}
          navTitles={navTitles}
        />
      )}
      <UpgradeCustodianMissingDetailsModal
        open={showMissingDetailsModal}
        onOpenChange={setShowMissingDetailsModal}
        onReviewFields={() => {
          setShowMissingDetailsModal(false);
          void router.push(`/advisor/dashboard/client/${salesforceAccountId}/details`);
        }}
        onContinueTransferring={() => {
          setShowMissingDetailsModal(false);
          void router.push(getUpgradeCustodianStepHref(salesforceAccountId, 'select-accounts'));
        }}
      />
    </>
  );
}
export default withLDConsumer()(ClientPageTitleNav);

export function HouseholdLastContactDate({ lastContactDate }: { readonly lastContactDate: string | null }) {
  if (!lastContactDate) {
    return null;
  }
  return (
    <div className="flex flex-row gap-0 justify-between">
      <Text variant="body-sm" color="secondary">
        Last contacted:
      </Text>
      <Text variant="body-sm" color="dark">
        {formatTimestampToRelative(lastContactDate)}
      </Text>
    </div>
  );
}

export function BillableAssetsDisplay({ household }: { readonly household: Household }) {
  if (!household.savvyAum) {
    return null;
  }
  return (
    <div className="flex flex-row gap-0 justify-between">
      <Text variant="body-sm" color="secondary">
        Billable Assets:
      </Text>
      <Text variant="body-sm" color="dark">
        {formatDollarAmount(household.savvyAum)}
      </Text>
    </div>
  );
}

export function AnnualRevenueDisplay({ household }: { readonly household: Household }) {
  if (!household.advisorAnnualRevenue) {
    return null;
  }

  const title = household.adviceOnly ? ADVICE_ONLY_CLIENT_ARR_TOOLTIP_TEXT : ARR_TOOLTIP_TEXT;

  return (
    <>
      <div className="flex flex-row gap-0 justify-between">
        <Text variant="body-sm" color="secondary">
          ARR:
        </Text>
        <Text variant="body-sm" color="dark">
          {formatDollarAmount(household.advisorAnnualRevenue)}
        </Text>
      </div>
      <Text variant="caption" color="secondary">
        {title}
      </Text>
    </>
  );
}

export function PotentialAUMDisplay({ household }: { readonly household: Household }) {
  if (!household.prospectData?.estimatedAum) {
    return null;
  }
  return (
    <div className="flex flex-row gap-0 justify-between">
      <Text variant="body-sm" color="secondary">
        Potential AUM:
      </Text>
      <Text variant="body-sm" color="dark">
        {formatLargeDollarAmount(Number(household.prospectData.estimatedAum))}
      </Text>
    </div>
  );
}

export function SavvyHouseholdIdDisplay({ householdId }: { readonly householdId: string | null | undefined }) {
  if (!householdId) {
    return null;
  }
  return (
    <div className="flex flex-row gap-0 justify-between">
      <Text variant="body-sm" color="secondary">
        Savvy Household Id:
      </Text>
      <Text variant="body-sm" color="dark">
        {householdId}
      </Text>
    </div>
  );
}

export function PrimaryClientUserIdDisplay({ userId }: { readonly userId: string | null | undefined }) {
  if (!userId) {
    return null;
  }
  return (
    <div className="flex flex-row gap-0 justify-between">
      <Text variant="body-sm" color="secondary">
        Primary Client User Id:
      </Text>
      <Text variant="body-sm" color="dark">
        {userId}
      </Text>
    </div>
  );
}
